import {memo, useCallback} from 'react';

import styled from 'styled-components';

import TrashIcon from '~/assets/images/icons/pie/functionalityTrashSmall.svg?react';
import {getLocalizationService} from '~/shared/services/localisationService';
import {
  creditCardTypesEnum,
  paymentCardsIconComponents,
} from '~/shared/utils/payments/paymentMethodsUtils';
import {smallBold, smallRegular} from '~/shared/theme/typography';
import PieButton from '~/shared/theme/pie/PieButton';
import {media} from '~/shared/theme/utils';

const Root = styled.div`
  position: relative;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${media.minLargeMobile`
    padding: 16px 0;
  `}
`;

const CardInfoWrapper = styled.div`
  display: flex;
  align-items: unset;
  gap: 16px;
  min-height: 44px;
`;

const CardTitle = styled.p`
  ${smallBold}
  color: ${({theme}) => theme.colors.charcoal80};
  margin-bottom: 4px;
`;

const CardExpiryDate = styled.p<{isFixedLTR: boolean}>`
  ${smallRegular}
  color: ${({theme}) => theme.colors.charcoal80};
  ${({isFixedLTR, theme}) => isFixedLTR && `
    direction: ltr;
    text-align: ${theme.isLTR ? 'right' : 'left'};
  `}
`;

const CardExpiredMessage = styled.p`
  ${smallRegular}
  color: ${({theme}) => theme.colors.contentError};
`;

const getCardTypeImage = (type: keyof typeof creditCardTypesEnum, brand?: keyof typeof creditCardTypesEnum) => {
  try {
    if (type === 'paypal') {
      return paymentCardsIconComponents[creditCardTypesEnum.paypal];
    }
    return brand && brand in creditCardTypesEnum ? paymentCardsIconComponents[creditCardTypesEnum[brand]] : paymentCardsIconComponents[creditCardTypesEnum.newcreditcard];
  } catch (error) {
    return paymentCardsIconComponents[creditCardTypesEnum.newcreditcard];
  }
};

type PaymentProps = {
  onRemovePaymentClick: (args: {id: number; cardName: string; type: string}) => void;
  id: number;
  dataTest?: string;
  type: keyof typeof creditCardTypesEnum;
  brand?: keyof typeof creditCardTypesEnum;
  suffix?: string;
  expiryDate?: string;
  email?: string; // used with PayPal only
  isExpired?: boolean;
};

const Payment = ({id, dataTest, type, brand, suffix, expiryDate, email, isExpired, onRemovePaymentClick}: PaymentProps) => {
  const {t} = getLocalizationService();

  const CardImage = getCardTypeImage(type, brand);
  const isPaypal = type === 'paypal';
  
  const removePayment = useCallback(() => {
    onRemovePaymentClick({id, cardName: isPaypal ? t('paypal') : t('card_xxx', {suffix}), type});
  }, [onRemovePaymentClick, id, isPaypal, t, suffix, type]);

  return (
    <Root data-test-id={dataTest}>
      <CardInfoWrapper>
        <CardImage className="avoid-fill-stroke" />
        <div>
          <CardTitle data-test-id="cardName">{isPaypal ? t('paypal') : suffix}</CardTitle>
          {!isExpired && (expiryDate || email) && <CardExpiryDate data-test-id="expDate" isFixedLTR={!!email}>{expiryDate || email}</CardExpiryDate>}
          {isExpired && <CardExpiredMessage data-test-id="expDate">{t('credit_card_expired')}</CardExpiredMessage>}
        </div>
      </CardInfoWrapper>
      <PieButton
        data-test-id="removePaymentBtn"
        onClick={removePayment}
        variant="outlineMedium"
        iconButton
      >
        <TrashIcon viewBox="0 0 16 16" width={20} height={20} />
      </PieButton>
    </Root>
  );
};

export default memo(Payment);
